import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { csv } from 'd3-fetch';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import './css/Visualization2.css';
import Visualization3 from './Visualization3';
import Visualization5 from './Visualization5';


function Visualization2() {
  const svgRef = useRef();
  const [data, setData] = useState([]);
  const [dateRange, setDateRange] = useState([new Date('2019-01-15').getTime(), new Date('2024-06-15').getTime()]);

  const minDate = new Date('2019-01-15').getTime();
  const maxDate = new Date('2024-06-15').getTime();

  useEffect(() => {
    csv(`${process.env.PUBLIC_URL}/data/monthly-co2-emissions-from-international-and-domestic-flights.csv`).then((csvData) => {
      const parsedData = csvData.map(d => ({
        date: d3.timeParse('%m/%d/%y')(d.Day),
        domestic: +d['Monthly CO2 emissions from domestic aviation'],
        international: +d['Monthly CO2 emissions from international aviation'],
      }));
      setData(parsedData);
    });
  }, []);

  useEffect(() => {
    if (data.length === 0) return;

    const filteredData = data.filter(d => d.date >= dateRange[0] && d.date <= dateRange[1]);

    const margin = { top: 20, right: 30, bottom: 70, left: 100 };
    const width = 800 - margin.left - margin.right;
    const height = 400 - margin.top - margin.bottom;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const x = d3.scaleTime()
      .domain(d3.extent(filteredData, d => d.date))
      .range([0, width]);

    const y = d3.scaleLinear()
      .domain([0, d3.max(filteredData, d => d.domestic + d.international)])
      .nice()
      .range([height, 0]);

    const internationalArea = d3.area()
      .x(d => x(d.date))
      .y0(y(0))
      .y1(d => y(d.international));

    const domesticArea = d3.area()
      .x(d => x(d.date))
      .y0(d => y(d.international))
      .y1(d => y(d.international + d.domestic));

    const xAxis = d3.axisBottom(x)
      .ticks(5)
      .tickFormat(d3.timeFormat('%b %Y'));

    const yAxis = d3.axisLeft(y);

    const chart = svg
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Add the areas
    chart.append('path')
      .datum(filteredData)
      .attr('fill', 'steelblue')
      .attr('d', internationalArea);

    chart.append('path')
      .datum(filteredData)
      .attr('fill', 'brown')
      .attr('d', domesticArea);

    // Add the x-axis
    chart.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(xAxis)
      .selectAll("text")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .attr("transform", "rotate(-45)");

    // Add the y-axis
    chart.append('g')
      .call(yAxis);

    // Add y-axis label
    chart.append('text')
      .attr('x', -height / 2)
      .attr('y', -margin.left + 20)
      .attr('transform', 'rotate(-90)')
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .text('Monthly CO2 Emissions (kg)');

    // Add x-axis label
    chart.append('text')
      .attr('x', width / 2)
      .attr('y', height + margin.bottom - 10)
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .text('Date');

    // Add legend to the top-right
    const legend = svg.append('g')
      .attr('transform', `translate(${width + margin.left - 100}, ${margin.top - 20})`); // Adjust legend position

    // Add legend items
    legend.append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', 12)
      .attr('height', 12)
      .attr('fill', 'brown');

    legend.append('text')
      .attr('x', 20)
      .attr('y', 10)
      .attr('text-anchor', 'start')
      .style('font-size', '12px')
      .text('Domestic');

    legend.append('rect')
      .attr('x', 0)
      .attr('y', 20)
      .attr('width', 12)
      .attr('height', 12)
      .attr('fill', 'steelblue');

    legend.append('text')
      .attr('x', 20)
      .attr('y', 30)
      .attr('text-anchor', 'start')
      .style('font-size', '12px')
      .text('International');
  }, [data, dateRange]);

  const handleSliderChange = (values) => {
    setDateRange(values);
  };

  const formatTooltip = (value) => {
    return d3.timeFormat('%b %d, %Y')(new Date(value));
  };

  const handleRender = (node, { value, dragging, index }) => {
    return (
      <div>
        {node}
        <div
          className={`slider-tooltip ${dragging ? 'dragging' : ''}`}
        >
          {formatTooltip(value)}
        </div>
      </div>
    );
  };

  return (
    <>
      <Visualization5 />
      <div className="header-container">
        <h2>
          Monthly CO₂ emissions from domestic and international
          commercial passenger flights, World, Jan 15, 2019 to Jun 15, 2024
        </h2>
        <p>
          Domestic aviation represents flights which depart and arrive within the same country. International aviation emissions are assigned
          to the country of departure.
        </p>
      </div>
      <div className="visualization-container">
        <div className="slider-container">
          <Slider
            range
            min={minDate}
            max={maxDate}
            value={dateRange}
            onChange={handleSliderChange}
            step={24 * 60 * 60 * 1000}
            marks={{
              [minDate]: formatTooltip(minDate),
              [maxDate]: formatTooltip(maxDate),
            }}
            handleRender={handleRender}
          />
        </div>
        <svg ref={svgRef} className="svg-container"></svg>
      </div>
      <Visualization3 />
    </>
  );
}

export default Visualization2;
